//@flow
import {
    zoneMap as zoneMapApi,
    zoneMapCreate as zoneMapCreateApi,
    zoneMapActiveApi,
    zoneMapVersionApi,
    zoneMapVersionsApi,
    usecaseMapApi,
    activeMapUsecasesApi,
    zoneMapVerify as zoneMapVerifyApi,
    disableZoneMap as zoneMapDisableApi,
    bulkZoneMapFetch as bulkZoneMapFetchApi,
    zoneMapVerifyAccessApi,
    validateEntityIds as validateEntityIdsApi
} from 'common/middleware/api';
import PaginatedEntityActions from 'common/redux/actions/PaginatedEntityActions';
import { showToast } from 'common/redux/actions/toast';
import DataUtils from 'common/utils/DataUtils';
import { ToastTypes } from 'common/components/core/toast/Toast';
import I18nHelper from 'common/helpers/I18nHelper';

import { LocusApiModel, ApiError } from 'common/modules/api';

import type { ZoneMap } from 'common/models/internal/ZoneMap';
import type { Dispatch } from 'redux';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

const ENTITY_NAME_ID = 'zoneMapMaster';

type Params = {
    clientId: string,
    mapId?: string
};

class ZoneMapActions extends PaginatedEntityActions<ZoneMap, Params> {
    activeApi: LocusApiModel;
    versionApi: LocusApiModel;
    usecaseApi: LocusApiModel;
    activeUsecasesApi: LocusApiModel;
    mapVerifyAccessApi: LocusApiModel;

    constructor(
        TAG: string,
        api: LocusApiModel,
        bulkFetchApi: LocusApiModel,
        disableApi: LocusApiModel,
        activeApi: LocusApiModel,
        validateEntityIdsApi: LocusApiModel,
        versionApi: LocusApiModel,
        usecaseMapApi: LocusApiModel,
        activeUsecasesApi: LocusApiModel,
        mapVerifyAccessApi: LocusApiModel
    ) {
        super(TAG, api, bulkFetchApi, disableApi, null, validateEntityIdsApi);
        this.activeApi = activeApi;
        this.versionApi = versionApi;
        this.usecaseApi = usecaseMapApi;
        this.activeUsecasesApi = activeUsecasesApi;
        this.mapVerifyAccessApi = mapVerifyAccessApi;
    }
    types = {
        ...this.types,
        ENTITY_VERSIONS_FETCH_START: 'ENTITY_VERSIONS_FETCH_START',
        ENTITY_VERSIONS_FETCH_SUCCESS: 'ENTITY_VERSIONS_FETCH_SUCCESS',
        ENTITY_VERSIONS_FETCH_FAILURE: 'ENTITY_VERSIONS_FETCH_FAILURE',
        VERIFY_MAP_START: 'VERIFY_MAP_START',
        VERIFY_MAP_SUCCESS: 'VERIFY_MAP_SUCCESS',
        VERIFY_MAP_ERROR: 'VERIFY_MAP_ERROR',
        USECASES_MAP_FETCH_SUCCESS: 'USECASES_MAP_FETCH_SUCCESS',
        SET_ACTIVE_USECASES_START: 'SET_ACTIVE_USECASES_START',
        SET_ACTIVE_USECASES_SUCCESS: 'SET_ACTIVE_USECASES_SUCCESS',
        SET_ACTIVE_USECASES_ERROR: 'SET_ACTIVE_USECASES_ERROR',
        URL_CHANGE: 'URL_CHANGE',
        MARK_MAP_ACTIVE: 'MARK_MAP_ACTIVE',
        VERIFY_MAP_ACCESS_FETCH_SUCCESS: 'VERIFY_MAP_ACCESS_FETCH_SUCCESS',
        VERSIONED_ZONE_FETCH_START: 'VERSIONED_ZONE_FETCH_START',
        VERSIONED_ZONE_FETCH_SUCCESS: 'VERSIONED_ZONE_FETCH_SUCCESS',
        VERSIONED_ZONE_FETCH_FAILURE: 'VERSIONED_ZONE_FETCH_FAILURE'
    };

    injectVersionedZone = (params, response) => {
        return {
            type: this.types.VERSIONED_ZONE_FETCH_SUCCESS,
            params,
            response
        };
    };

    entityVersionsFetchStart = params => {
        return {
            type: this.types.ENTITY_VERSIONS_FETCH_START,
            params
        };
    };

    fetchVersionedZoneStart = params => {
        return {
            type: this.types.VERSIONED_ZONE_FETCH_START,
            params
        };
    };

    fetchVersionedZoneFailure = params => {
        return {
            type: this.types.VERSIONED_ZONE_FETCH_FAILURE,
            params
        };
    };

    injectEntityVersions = (params, response) => {
        return {
            type: this.types.ENTITY_VERSIONS_FETCH_SUCCESS,
            params,
            response
        };
    };

    entityVersionsFetchError = (params, error) => {
        return {
            type: this.types.ENTITY_VERSIONS_FETCH_FAILURE,
            params,
            error
        };
    };

    startVerification = (params, body) => {
        return {
            type: this.types.VERIFY_MAP_START,
            params,
            body
        };
    };
    verificationSuccess = (params, body) => {
        return {
            type: this.types.VERIFY_MAP_SUCCESS,
            params,
            body
        };
    };
    verificationError = (params, body, error) => {
        return {
            type: this.types.VERIFY_MAP_ERROR,
            params,
            body,
            error
        };
    };

    usecasesMapFetchSuccess = (params, response) => {
        return {
            type: this.types.USECASES_MAP_FETCH_SUCCESS,
            params,
            response
        };
    };
    setActiveUsecasesStart = params => {
        return {
            type: this.types.SET_ACTIVE_USECASES_START,
            params
        };
    };
    setActiveUsecasesSuccess = (params, usecases) => {
        return {
            type: this.types.SET_ACTIVE_USECASES_SUCCESS,
            params,
            usecases
        };
    };

    fetchVerifyAccessSuccess = (params, response) => {
        return {
            type: this.types.VERIFY_MAP_ACCESS_FETCH_SUCCESS,
            params,
            response
        };
    };

    fetchEntityVersions = params => {
        return (dispatch: Dispatch<*>) => {
            dispatch(this.entityVersionsFetchStart(params));
            zoneMapVersionsApi
                .fetch(params)
                .then(response => {
                    dispatch(this.injectEntityVersions(params, response));
                })
                .catch(error => {
                    if (error instanceof ApiError) {
                        dispatch(this.entityVersionsFetchError(params, error));
                        return;
                    }
                    throw error;
                });
        };
    };

    fetchEntity = params => {
        const { active, versionId, ...otherParams } = params;
        if (active) {
            return this.fetchActiveEntity(otherParams);
        }
        if (!DataUtils.isEmpty(versionId)) {
            return this.fetchEntityVersion({ ...otherParams, versionId });
        }
        return (dispatch: Dispatch<*>) => {
            dispatch(this.emitAddEntityStart(params));
            this.api
                .fetch(otherParams)
                .then(entity => {
                    dispatch(this.injectEntity(params, entity));
                })
                .catch(error => {
                    dispatch(this.emitAddEntityFailure(params, error));
                    if (error) {
                        const toast = {
                            message: error.message,
                            type: ToastTypes.Error
                        };
                        if (error instanceof ApiError) {
                            dispatch(showToast(toast, params));
                        } else {
                            throw error;
                        }
                    }
                });
        };
    };

    fetchEntityVersion = params => {
        return (dispatch: Dispatch<*>) => {
            dispatch(this.emitAddEntityStart(params));
            this.versionApi
                .fetch(params)
                .then(entity => {
                    dispatch(this.injectEntity(params, entity));
                })
                .catch(error => {
                    dispatch(this.emitAddEntityFailure(params, error));
                    if (error instanceof ApiError) {
                        const toast = {
                            message: error.message,
                            type: ToastTypes.Error
                        };
                        dispatch(showToast(toast, params));
                        return;
                    }
                    throw error;
                });
        };
    };

    fetchVersionedZone = params => {
        return (dispatch: Dispatch<*>) => {
            dispatch(this.fetchVersionedZoneStart(params));
            this.versionApi
                .fetch(params)
                .then(map => {
                    dispatch(this.injectVersionedZone(params, map));
                })
                .catch(error => {
                    dispatch(this.fetchVersionedZoneFailure(params));
                    if (error instanceof ApiError) {
                        const toast = {
                            message: error.message,
                            type: ToastTypes.Error
                        };
                        dispatch(showToast(toast, params));
                        return;
                    }
                    throw error;
                });
        };
    };
    fetchActiveEntity = params => {
        return (dispatch: Dispatch<*>) => {
            dispatch(this.emitAddEntityStart(params));
            this.activeApi
                .fetch(params)
                .then(entity => {
                    dispatch(this.injectEntity(params, entity));
                })
                .catch(error => {
                    dispatch(this.emitAddEntityFailure(params, error));
                    if (error) {
                        const toast = {
                            message: error.message,
                            type: ToastTypes.Error
                        };
                        if (error instanceof ApiError) {
                            dispatch(showToast(toast, params));
                        } else {
                            throw error;
                        }
                    }
                });
        };
    };

    createEntity = (params, entity, options = {}) => {
        const { onSuccess } = options;
        return (dispatch: Dispatch<*>) => {
            dispatch(this.emitAddEntityStart(params));
            zoneMapCreateApi
                .post(params, entity.toJS())
                .then((response: any) => {
                    onSuccess && onSuccess(response);
                    dispatch(this.injectEntity(params, response));
                    return response;
                })
                .catch((error: Error) => {
                    dispatch(this.emitAddEntityFailure(params, error));
                    this.handleError(error, options, dispatch);
                });
        };
    };

    verifyEntities = (params, body, options = {}) => {
        const { onSuccess, onFailure } = options;
        return (dispatch: Dispatch<*>) => {
            dispatch(this.startVerification(params, body));
            zoneMapVerifyApi
                .post(params, { requests: body.toJS() })
                .then(response => {
                    onSuccess && onSuccess(response);
                    dispatch(this.injectEntity(params, response));
                    dispatch(this.verificationSuccess(params, response));
                    return response;
                })
                .catch(error => {
                    dispatch(this.verificationError(params, body, error));
                    onFailure && onFailure(error);
                    this.handleError(error, options, dispatch);
                });
        };
    };

    changeActiveStatus = (
        params: { clientId: string, mapId: string },
        version: number,
        active: boolean
    ) => {
        return (dispatch: Dispatch<*>) => {
            const body = {
                version,
                active
            };
            return this.activeApi.post(params, body).then(() => {
                dispatch({
                    type: this.types.MARK_MAP_ACTIVE,
                    params: { ...params, version, active }
                });
            });
        };
    };

    superDisableEntity = this.disableEntity;

    disableEntity = (
        params: { clientId: string, mapId: string },
        _options = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (map: ZoneMap) => {
                        const toast = {
                            message: nsTranslate('deleteEntitySuccessMessage', {
                                entityNameId: ENTITY_NAME_ID
                            }),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(map);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    }
                };
                dispatch(this.superDisableEntity(params, options));
            });
        };
    };

    fetchUsecasesMap = (params: { clientId: string }) => {
        return (dispatch: Dispatch<*>) => {
            return this.usecaseApi
                .fetch(params)
                .then(response => {
                    dispatch(this.usecasesMapFetchSuccess(params, response));
                })
                .catch(error => {
                    const toast = {
                        message: error.message,
                        type: ToastTypes.Error
                    };

                    dispatch(showToast(toast, params));
                });
        };
    };

    changeActiveUsecases = (
        params: { clientId: string, mapId: string },
        usecases: array,
        options = {}
    ) => {
        const { onSuccess } = options;
        return (dispatch: Dispatch<*>) => {
            const body = { usecases };
            dispatch(this.setActiveUsecasesStart(params));
            return this.activeUsecasesApi
                .post(params, body)
                .then(() => {
                    dispatch(this.setActiveUsecasesSuccess(params, usecases));
                    onSuccess && onSuccess();
                })
                .catch(error => {
                    const toast = {
                        message: error.message,
                        type: ToastTypes.Error
                    };

                    dispatch(showToast(toast, params));
                });
        };
    };

    fetchVerifyAccess = (params: { clientId: string }) => {
        return (dispatch: Dispatch<*>) => {
            return this.mapVerifyAccessApi
                .fetch(params)
                .then(response =>
                    dispatch(this.fetchVerifyAccessSuccess(params, response))
                )
                .catch(error => {
                    const toast = {
                        message: error.message,
                        type: ToastTypes.Error
                    };

                    dispatch(showToast(toast, params));
                });
        };
    };
}

export default new ZoneMapActions(
    'ZONE_MAP',
    zoneMapApi,
    bulkZoneMapFetchApi,
    zoneMapDisableApi,
    zoneMapActiveApi,
    validateEntityIdsApi,
    zoneMapVersionApi,
    usecaseMapApi,
    activeMapUsecasesApi,
    zoneMapVerifyAccessApi
);
